/**
 * BridgeFieldsForm — the patient-side "fields you need to fill" page.
 *
 * Fetches the encrypted PDF + encrypted fields-schema from the bridge,
 * decrypts both using the symmetric key the magic link carried in its
 * URL fragment, renders the PDF for context, and exposes the
 * physician-skipped fields as a side panel. Patients tap a field name,
 * a modal opens, they type their value, and the panel updates.
 *
 * When all required fields are filled, the patient submits via the
 * parent BridgeOnboardingPage (this component just owns the data; the
 * parent owns the seal-and-PUT step).
 *
 * V1 keeps things deliberately simple: no PDF-overlay positioning, no
 * canvas drawing on top of the PDF, no field-bbox highlighting. The
 * patient sees the PDF for context and fills in the side panel. A
 * later iteration can layer overlay positioning on top once the Mac
 * dispatch starts including field bboxes in the encrypted schema.
 */

import { DEFAULT_CONFIRM_STATEMENT } from "./signingStatements";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
// Bundled worker (Vite ?url asset), legacy build for the es2015 target.
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import {
  formRefusalOf,
  getBridgeSession,
  openSecretBox,
  type FormRefusalCode,
} from "../../lib/bridgeClient";
// ─────────────────────────────────────────────────────────────────────
// PROFILE-EDITOR INTEGRATION (Agent B — demographics review/edit step)
// Adds a pre-sign "review your info" view. ProfileEditor takes over the
// left panel until the patient clicks "Continue to sign", then the
// existing fill+sign UI is revealed. The edited profile + the
// update_my_file flag flow back to the parent through onComplete's
// 3rd argument. Agent C's signature-glow change targets the signature
// input markup further down — that block is left untouched here.
// ─────────────────────────────────────────────────────────────────────
import ProfileEditor from "./ProfileEditor";
import BridgeQuestionnaire from "./BridgeQuestionnaire";
import buildQuestionnaire, { renderBackendQuestions } from "../../lib/bridgeQuestionnaire";
import { batchStatuses } from "../../lib/fieldBatches";
import computeSignReadiness from "../../lib/signReadiness";
import signerAudiencesFor from "../../lib/signerAudience";
import { deriveInitialSignatureName, deriveInitialPlanMemberSignatureName } from "../../lib/signatureName";
import { typedSignatureToPng } from "../../lib/typedSignature";
import {
  prefilledValues,
  resolveSignatureDateField,
  seedSchemaDemographics,
  signatureLinesFrom,
  untouchedBaseline,
} from "../../lib/bridgeSchema";
import {
  FORMATS,
  formatValue,
  maskedChange,
  isFormatValid,
  validationMessage,
  inferInputFormat,
} from "../../shared/lib/inputFormats";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import type {
  SchemaField,
  BridgeQuestion,
  PatientProfile,
  BBox,
} from "../../types/shared";

// Load the PDF.js worker from the app bundle (Vite ?url asset above),
// not a runtime CDN. Removes the undeclared third-party cdnjs dependency
// from this patient-facing app and always version-matches pdfjs-dist.
// The hashed worker asset is emitted under /static/ and served by the
// bridge (StaticFiles mount) and the main app alike.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PDF_PAGE_MAX_WIDTH = 760;

const STATUS = {
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

// Local {name, page, bbox} click-target shape derived from schema v2 entries.
interface ClickTarget {
  name: string;
  page: number;
  bbox: BBox;
  type?: string | null;
}

// Signature field {name, page, bbox} located from schema v2.
interface SignatureTargetShape {
  name: string;
  page: number;
  bbox: BBox;
}

// Panel→PDF wayfinding highlight target (may also carry the signature kind).
interface ActiveTargetShape {
  page: number;
  bbox: BBox;
  label?: string;
  kind?: string;
}

// The demographics-review sub-state relayed up from ProfileEditor.onChange.
interface ProfileChangePayload {
  profile: PatientProfile;
  updateMyFile: boolean;
  editedKeys: string[];
}

// Signature payload shape passed to onComplete.
interface SignaturePayload {
  name: string;
  font: string;
  image_b64: string | null;
  source: string;
}

// Profile patch bridged through onComplete's 3rd argument.
interface ProfilePatch {
  profile: PatientProfile;
  update_my_file: boolean;
  edited_keys: string[];
}

interface BridgeFieldsFormProps {
  onComplete?: (
    values: Record<string, string>,
    signature: SignaturePayload,
    profilePatch: ProfilePatch | null
  ) => void;
  defaultSignatureName?: string;
  defaultSignatureFont?: string;
  initialProfile?: PatientProfile | null;
  skipProfileReview?: boolean;
  onBack?: (() => void) | null;
  /** What the signer confirms by ticking the (first) signature box. The plan
   * member's leg passes its own statement, which also covers the accuracy of
   * their section (imp_93b41233); everyone else keeps the default. */
  confirmStatement?: string;
  /** The plan member signs only their own section of someone else's form, so
   * the review step must not address them as the patient. */
  isPlanMember?: boolean;
  /** The bridge refused the form itself for its fee (409 `fee_required` /
   * `fee_declined` / `request_closed` on the fields-schema or the PDF;
   * ruling 2026-10-14). The page routes on the code; without this prop the
   * refusal shows as the plain load error it always was. */
  onRefused?: (code: FormRefusalCode) => void;
}




export default function BridgeFieldsForm({
  onComplete,
  defaultSignatureName = "",
  defaultSignatureFont = "Dancing Script",
  // Optional preloaded snake_case profile from the Mac. When present, a
  // ProfileEditor step is shown first; when absent, the component
  // behaves exactly like before. Keeps the parent in control of whether
  // demographics review is part of this assignment's flow.
  initialProfile = null,
  // Skip the ProfileEditor review on mount. Used by returning patients
  // (onboarding_complete=true) who don't need to re-confirm demographics
  // every dispatch. They can still re-open the editor on demand.
  skipProfileReview = false,
  // Go back to the previous step (Confirm Details). Surfaced on the leading
  // questionnaire as a "← Back" link.
  onBack = null,
  confirmStatement = DEFAULT_CONFIRM_STATEMENT,
  isPlanMember = false,
  onRefused,
}: BridgeFieldsFormProps) {
  const session = useMemo(() => getBridgeSession(), []);
  // Read through a ref so the load effect's dependencies stay as they were.
  const onRefusedRef = useRef(onRefused);
  onRefusedRef.current = onRefused;
  const [status, setStatus] = useState(STATUS.LOADING);
  // The layout stacks on phones (PDF preview ABOVE the fields) but sits
  // side-by-side on tablet/desktop — the instruction copy must say where the
  // preview actually is ("above" vs "on the right").
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
    };
  }, []);
  const previewWhere = isMobile ? "above (tap to expand)" : "on the right";
  const reviewHeading = isPlanMember ? "The plan member section" : "Your prior authorization form";
  const reviewIntro = isPlanMember
    ? "The patient's doctor has prepared this coverage request. You are completing and signing only the plan member section. Please:"
    : "Your physician has prepared this form for your insurance. Please:";
  const [error, setError] = useState<string | null>(null);
  const [requiredFields, setRequiredFields] = useState<SchemaField[]>([]);
  // Raw decrypted schema fields (with the durable question-model keys:
  // audience/stem/patient_stem/section/conditionalOn). Feeds the leading
  // questionnaire step; requiredFields stays the trimmed side-panel list.
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  // The question-model the BACKEND built (sealed payload `questions`). When
  // present this is the single source of truth for the questionnaire; the client
  // only renders it + evaluates live visibility. Null for assignments sealed
  // before the backend emitted it → the client falls back to building from
  // schemaFields.
  const [backendQuestions, setBackendQuestions] = useState<BridgeQuestion[] | null>(null);
  // The patient answers a plain-language questionnaire FIRST (when the form
  // carries patient-owned questions), then reviews + signs. One-shot: once
  // they continue, we don't re-show it.
  const [questionnaireDone, setQuestionnaireDone] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  // Snapshot of values pre-filled from the bridge schema. Used at submit
  // time to compute the *edited* subset, so unchanged pre-fills don't
  // round-trip back to the canonical and clobber the physician's longer
  // value with a fragment the patient retyped.
  const [initialValues, setInitialValues] = useState<Record<string, string>>({});
  const [modalField, setModalField] = useState<SchemaField | null>(null);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Callback ref so the PDF render re-fires whenever the canvas actually
  // ATTACHES — not just when pdfDoc/currentPage/status change. The canvas
  // mounts late (after the questionnaire step is dismissed / the view
  // switches), and a pure dep-list missed that, leaving page 1 blank until
  // "Next" was clicked. This is the recurring "bug #1". `canvasEl` is added to
  // the render effect's deps so attaching triggers a render.
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const attachCanvas = useCallback((el: HTMLCanvasElement | null) => {
    canvasRef.current = el;
    setCanvasEl(el);
  }, []);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  // Patient-typed signature. Type-only since 2026-08-26 — drawing was removed
  // because drawn marks rasterized poorly on the faxed PDF. Pre-filled from the
  // profile (defaultSignatureName, else an initialProfile first+last fallback)
  // so the patient lands on their own name; they must still explicitly confirm
  // before submit, and the field stays editable. The typed name is rendered in
  // a cursive font and rasterized to a PNG (typedSignatureToPng) for the
  // signature box.
  // A plan holder rendered here seeds from their own plan_member_* name only,
  // never the patient's first name the plan-member preload carries
  // (imp_797e5495).
  const [signatureName, setSignatureName] = useState(() =>
    isPlanMember
      ? deriveInitialPlanMemberSignatureName(defaultSignatureName, initialProfile as any)
      : deriveInitialSignatureName(defaultSignatureName, initialProfile)
  );
  const [signatureFont, setSignatureFont] = useState(defaultSignatureFont);
  const [signatureConfirmed, setSignatureConfirmed] = useState<boolean>(false);

  // ── BEGIN CLICK-TO-JUMP (Agent B) ───────────────────────────────────
  // Click-to-jump state: list of {name, page, bbox} click targets from
  // schema v2, the canvas-pixel scale used to position the overlay,
  // and the currently focused ProfileEditor field key. See the overlay
  // block in the PDF area + the focus useEffect inside ProfileEditor.jsx
  // for the receiving side.
  const [pdfClickTargets, setPdfClickTargets] = useState<ClickTarget[]>([]);
  // Signature field {name, page, bbox} from schema v2 — drives the "sign here"
  // highlight on the PDF (not a fillable input).
  const [signatureTarget, setSignatureTarget] = useState<SignatureTargetShape | null>(null);
  // EVERY signature line this signer owns (a form can ask the same signer to
  // sign in more than one place — main + consent section on the Desjardins
  // Mounjaro PA). Each must be signed EXPLICITLY before submit; nothing is
  // auto-copied (Michael, 2026-09-03, imp_31e369a1: "sign here, then move to
  // the next area and sign here as well — both signatures are needed").
  // Derived from schema v2 fields; empty for v1 schemas (legacy single
  // confirmation preserved).
  const [signatureTargets, setSignatureTargets] = useState<SignatureTargetShape[]>([]);
  // Which signature lines have been explicitly signed (by field name).
  const [signedSigs, setSignedSigs] = useState<Record<string, boolean>>({});
  const multiSig = signatureTargets.length > 1;
  // The next line awaiting a signature — drives the "Sign here" cue.
  const nextUnsignedTarget = multiSig
    ? signatureTargets.find((t) => !signedSigs[t.name]) || null
    : signatureTarget;
  // The gate: single-line forms keep the one confirmation checkbox; multi-line
  // forms are confirmed only when EVERY line is signed.
  const allSignaturesConfirmed = multiSig
    ? signatureTargets.every((t) => !!signedSigs[t.name])
    : signatureConfirmed;
  const [pdfScale, setPdfScale] = useState(1);
  const [focusFieldKey, setFocusFieldKey] = useState<{ key: string; nonce: number } | null>(null);

  function jumpToProfileField(pdfFieldName: string) {
    const profileKey = _pdfFieldNameToProfileKey(pdfFieldName);
    if (!profileKey) return; // unknown / non-mapped field — no-op
    if (viewMode !== "profile") setViewMode("profile");
    // Bump a fresh object each click so an immediate re-click on the same
    // field still triggers ProfileEditor's effect (React bails on identical
    // primitive values otherwise).
    setFocusFieldKey({ key: profileKey, nonce: Date.now() });
  }
  // ── END CLICK-TO-JUMP ───────────────────────────────────────────────

  // ── PANEL → PDF wayfinding ──────────────────────────────────────────
  // When the patient focuses/hovers a side-panel field, jump the PDF to that
  // field's page and pulse-highlight its box on the form, so they can see
  // exactly what they're filling. Same for the signature section.
  const [activeTarget, setActiveTarget] = useState<ActiveTargetShape | null>(null);
  function focusFormField(name: string, label?: string) {
    const t = (pdfClickTargets || []).find((x) => x.name === name);
    if (!t) return; // field has no located bbox — nothing to highlight
    setActiveTarget({ page: t.page, bbox: t.bbox, label });
    setCurrentPage(t.page);
  }
  // Click-to-sign from the PDF: highlight + page-jump to the signature field,
  // then scroll the signer panel into view so the patient can type/draw. Mirrors
  // the physician's click-to-sign affordance.
  const signerRef = useRef<HTMLDivElement | null>(null);
  function handleSignClick() {
    focusSignature();
    signerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function focusSignature() {
    // Multi-line forms: cue the NEXT unsigned line so the signer is walked
    // from one signature area to the next.
    const t = nextUnsignedTarget || signatureTarget;
    if (!t) return;
    setActiveTarget({ page: t.page, bbox: t.bbox, label: "Sign here", kind: "signature" });
    setCurrentPage(t.page);
  }
  function focusSignatureLine(t: SignatureTargetShape) {
    if (!t || !Array.isArray(t.bbox)) return;
    setActiveTarget({ page: t.page, bbox: t.bbox, label: "Sign here", kind: "signature" });
    setCurrentPage(t.page);
  }

  // Stamp (or clear) the date line paired with one signature field, so the
  // date appears NEXT TO that signature the moment it's signed. The key must
  // match the schema's actual field name — deriving it by string surgery
  // stranded the value on an orphan key whenever the dispatched schema's
  // casing differed (v1 dispatches keyed lowercase machine_names; v2 uses
  // Title_Case names), and the date silently never rendered. Resolve
  // candidates against schemaFields case-insensitively; suffix blocks (_B1)
  // and plan-member signers keep working via the candidate forms.
  function stampSigDate(sigFieldName: string | undefined, on: boolean) {
    const dateField = resolveSignatureDateField(sigFieldName, schemaFields);
    if (on) {
      // ISO, like every stored date: a month-first string is ambiguous to the
      // Mac's normalizer (10/08 — October or August?) and landed verbatim in
      // the plan member's date box on the 2026-10-08 round trip.
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      setValue(dateField, `${d.getFullYear()}-${mm}-${dd}`);
    } else {
      // Un-signing clears the date so a dated-but-unsigned line never reads
      // as already signed.
      setValue(dateField, "");
    }
  }

  // ── Mobile PDF collapse (responsive UX) ─────────────────────────────
  // On mobile the PDF stacks on top of the form fields. Default to
  // collapsed so the patient lands on the fillable controls instead of
  // scrolling past the PDF. Desktop/tablet ignore this flag entirely
  // (the CSS media queries always show the PDF panel side-by-side).
  const [pdfCollapsedMobile, setPdfCollapsedMobile] = useState(true);

  // ── Profile-editor sub-state ────────────────────────────────────────
  // `viewMode` controls which left-panel view is active. When there's no
  // initialProfile we skip straight to "fill" (legacy behaviour). When
  // there IS one, the patient sees "profile" first and clicks
  // "Continue to sign" to advance.
  const [viewMode, setViewMode] = useState<"profile" | "fill">(
    initialProfile && !skipProfileReview ? "profile" : "fill"
  );
  const [editedProfile, setEditedProfile] = useState<PatientProfile | null>(initialProfile || null);
  const [updateMyFile, setUpdateMyFile] = useState<boolean>(true);
  const [editedKeys, setEditedKeys] = useState<string[]>([]);

  function handleProfileChange({ profile, updateMyFile: flag, editedKeys: keys }: ProfileChangePayload) {
    setEditedProfile(profile);
    setUpdateMyFile(flag);
    setEditedKeys(keys);
  }

  useEffect(() => {
    if (!session) {
      setError("Your session expired. Ask your provider for a fresh link.");
      setStatus(STATUS.ERROR);
      return;
    }
    if (!session.symmetricKeyB64) {
      // No encryption key means there's nothing to decrypt — surface the
      // form review screen so the patient can still confirm and sign.
      setStatus(STATUS.READY);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const headers = { "X-Patient-Token": session!.sessionToken as string };

        // Schema first — empty is fine; the patient still reviews the PDF
        // and signs explicitly. We no longer auto-submit on empty schema.
        const schemaResp = await fetch(
          `/api/bridge/assignments/${encodeURIComponent(session!.assignmentId as string)}/fields-schema`,
          { method: "GET", headers, credentials: "omit" }
        );
        if (!schemaResp.ok) {
          const refusal = await formRefusalOf(schemaResp);
          if (refusal && onRefusedRef.current) {
            if (!cancelled) onRefusedRef.current(refusal);
            return;
          }
          throw new Error(`fields-schema fetch failed (${schemaResp.status})`);
        }
        const schemaBody = await schemaResp.json();
        let schemaFields: any[] = [];
        let schemaVersion = 1;
        let signatureField: SignatureTargetShape | null = null;
        // WHICH SUBJECTS THE READER OF THIS LEG IS (backend
        // `questionnaire_builder.reader_subjects`). Absent on payloads sealed
        // before 2026-09-18 → both, which is the old behaviour exactly.
        let readerSubjects: string[] = ["patient", "plan_member"];
        if (schemaBody.encrypted_b64) {
          const plaintext = openSecretBox(
            schemaBody.encrypted_b64,
            session!.symmetricKeyB64 as string
          );
          const parsed = JSON.parse(new TextDecoder().decode(plaintext));
          schemaFields = Array.isArray(parsed?.fields) ? parsed.fields : [];
          if (Number(parsed?.schema_version) >= 2) schemaVersion = 2;
          if (Array.isArray(parsed?.reader_subjects) && parsed.reader_subjects.length) {
            readerSubjects = parsed.reader_subjects.map((x: any) => String(x));
          }
          // Backend-built question-model (single source of truth). Present on
          // assignments sealed by a current backend; absent on older ones.
          setBackendQuestions(Array.isArray(parsed?.questions) ? parsed.questions : null);
          const sf = parsed?.signature_field;
          if (sf && Array.isArray(sf.bbox) && sf.bbox.length >= 4 && Number.isFinite(Number(sf.page))) {
            signatureField = { name: sf.name, page: Number(sf.page), bbox: sf.bbox.map((v: any) => Number(v)) };
          }
        }
        if (cancelled) return;

        // Normalize schema entries: strings become {name} stubs.
        const normalized = schemaFields.map((f) =>
          typeof f === "string"
            ? { name: f, label: _humanize(f), required: false }
            : {
                name: f.name,
                // Required ONLY when the model says so explicitly. A missing
                // `required` key means optional — matching the questionnaire
                // consumer (bridgeQuestionnaire.js `!!f.required`). The old
                // `!== false` default forced every un-modeled field mandatory,
                // trapping the patient behind phantom-required fields ("can't
                // sign"). See lib/signReadiness.js.
                label: f.label || _humanize(f.name),
                required: f.required === true,
                // Carry the durable model so the review panel can apply the
                // same conditional + audience logic the questionnaire uses.
                conditionalOn: f.conditionalOn || null,
                audience: f.audience || null,
              }
        );
        setRequiredFields(normalized);

        // Seed confirmed demographics (from Step 1 "Confirm Details") into the
        // schema field values so the questionnaire treats them as already
        // answered — showing them on review instead of re-asking name / DOB /
        // address / etc. Without this, the provider's demographics never reach
        // the questionnaire's value check, so every demographic was re-asked.
        seedSchemaDemographics(schemaFields, initialProfile, readerSubjects);

        // Keep the full schema (with the question-model keys) for the
        // leading questionnaire step.
        setSchemaFields(schemaFields);

        // Pre-fill from schema v2's `value` keys. Treat pre-filled rows as
        // "accepted by default" — the patient can edit, but if they don't,
        // the submit path below excludes them so we never re-write the
        // canonical with the same value (or worse, a fragment of it). The
        // first page's plan answers are the exception: they go back with
        // the submission, because nothing else writes them onto the form.
        const prefilled = prefilledValues(schemaFields);
        if (Object.keys(prefilled).length > 0) {
          setValues((prev) => ({ ...prefilled, ...prev }));
          setInitialValues(untouchedBaseline(schemaFields));
        }

        // ── BEGIN CLICK-TO-JUMP (Agent B) ───────────────────────────
        // Collect entries with usable {page, bbox} for the overlay. v1
        // schemas (string-only) leave this empty → no overlay rendered,
        // legacy behaviour preserved.
        if (schemaVersion >= 2) {
          const targets = schemaFields
            .filter(
              (f) =>
                f &&
                typeof f === "object" &&
                Array.isArray(f.bbox) &&
                f.bbox.length >= 4 &&
                Number.isFinite(Number(f.page))
            )
            .map((f) => ({
              name: f.name,
              page: Number(f.page),
              bbox: f.bbox.map((v: any) => Number(v)),
              type: f.type || null,
            }));
          setPdfClickTargets(targets);
        } else {
          setPdfClickTargets([]);
        }
        setSignatureTarget(schemaVersion >= 2 ? signatureField : null);
        // Every signature line this signer owns, ordered by page then
        // position, each to be signed explicitly (imp_31e369a1). Date lines
        // are excluded — they're stamped automatically when their signature
        // line is signed.
        setSignatureTargets(signatureLinesFrom(schemaFields, schemaVersion, signatureField));
        // ── END CLICK-TO-JUMP ───────────────────────────────────────

        // Fetch + decrypt the PDF (best-effort; missing PDF is fine).
        const pdfResp = await fetch(
          `/api/bridge/assignments/${encodeURIComponent(session!.assignmentId as string)}/pdf`,
          { method: "GET", headers, credentials: "omit" }
        );
        if (!pdfResp.ok) {
          // Refused for its fee between the schema and the PDF (a decline
          // in another tab): route it, never render a form that is closed.
          const refusal = await formRefusalOf(pdfResp);
          if (refusal && onRefusedRef.current) {
            if (!cancelled) onRefusedRef.current(refusal);
            return;
          }
        }
        if (pdfResp.ok) {
          const body = await pdfResp.json();
          if (body.encrypted_b64) {
            try {
              const pdfBytes = openSecretBox(
                body.encrypted_b64,
                session!.symmetricKeyB64 as string
              );
              // Stash the decrypted bytes so the post-submit "Download a copy"
              // button works even after the bridge locks the assignment (the
              // re-fetch 401s once submitted). Survives this component unmount.
              try { (window as any).__hfFormPdfBytes = pdfBytes; } catch (_) {}
              const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
              const url = URL.createObjectURL(blob);
              if (cancelled) {
                URL.revokeObjectURL(url);
                return;
              }
              setPdfBlobUrl(url);
              const doc = await pdfjsLib.getDocument(url).promise;
              if (cancelled) return;
              setPdfDoc(doc);
              setNumPages(doc.numPages);
              setCurrentPage(1);
            } catch (pdfErr) {
              console.warn("PDF decrypt/render failed:", pdfErr);
              // Carry on without the PDF — patient still gets the side panel.
            }
          }
        }
        if (cancelled) return;
        setStatus(STATUS.READY);
      } catch (err) {
        console.error("BridgeFieldsForm load failed:", err);
        if (cancelled) return;
        setError((err as Error).message || String(err));
        setStatus(STATUS.ERROR);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [onComplete, session]);

  // Re-render on window resize so the PDF overlay recomputes its display scale
  // when the canvas shrinks/grows (moving between full- and half-width windows).
  const [, _bumpResize] = useState(0);
  useEffect(() => {
    const onResize = () => _bumpResize((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Render the current PDF page whenever the doc, page, or canvas mounts.
  // `status` is in the dep list so this re-fires after LOADING→READY:
  // pdfDoc loads inside the load() effect *before* setStatus(READY), so the
  // canvas isn't mounted on the first run and the effect bails. Without the
  // status dep, only navigating to a different page (which changes
  // currentPage) ever triggered a successful render — that was bug #1
  // ("page 1 blank until clicking Next").
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      const page = await pdfDoc.getPage(currentPage);
      if (cancelled) return;
      const viewport0 = page.getViewport({ scale: 1 });
      const scale = Math.min(
        PDF_PAGE_MAX_WIDTH / viewport0.width,
        2.0
      );
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;
      // ── CLICK-TO-JUMP: surface scale to the overlay layer below.
      if (!cancelled) setPdfScale(scale);
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage, status, canvasEl]);

  // Free the Blob URL when the component unmounts.
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  function setValue(name: string, value: string) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  // ── Sign gate — single source of truth, shared with the test harness ──
  // Which fields actually apply (audience + conditional + Yes/No-pair), which
  // are genuinely required, and whether the signature gate is satisfied — all
  // decided by lib/signReadiness.js so the panel and the unit tests can never
  // diverge. (That divergence — the panel defaulting a missing `required` to
  // true while the questionnaire defaulted it false — is what trapped patients
  // behind phantom-required fields, "I filled the form but it won't let me
  // sign".) A stale/partial schema that isn't modeled hard-blocks nothing.
  // Which audiences THIS session answers for. On a split-link send each signer
  // gets one ("patient" / "plan_holder"); on a single-signer send the bridge
  // stamps role "legacy" and this one person owns BOTH — the patient is also
  // the plan member, so their contract-number / member-ID / plan-sponsor asks
  // must not be filtered out of the only link that was sent. Derived in
  // lib/signerAudience, which mirrors the backend's `patient_schema_audiences`.
  // Memoized: it feeds the questionnaire useMemo's dependency array, and a
  // fresh array on every render would rebuild the questionnaire on each keystroke.
  const signerAudience = useMemo(
    () => signerAudiencesFor(session?.role as string | undefined),
    [session]
  );
  const {
    panelFields,
    requiredPanel,
    requiredFilledCount,
    allRequiredFilled,
    schemaModeled,
    signatureReady,
    canSubmit,
    formatBlocking,
  } = computeSignReadiness({
    schemaFields,
    values,
    signerAudience,
    signature: {
      // Multi-line forms are confirmed only when EVERY signature line has
      // been explicitly signed (imp_31e369a1).
      confirmed: allSignaturesConfirmed,
      name: signatureName,
    },
  });
  // Per-row required styling in the panel (orange = required-but-empty).
  const fieldRequired = (f: SchemaField) => schemaModeled && !!f.required;

  // A FILLED field whose value fails its wired format blocks Sign & submit.
  // Because it is filled, the panel's required-but-empty styling never touched
  // it: the row wore a green tick while the submit line said "fix the
  // highlighted field" — pointing at a highlight that was never rendered, with
  // the offending row scrolled out of view. Dead end with no exit
  // (case imp_c9f5f20e). Carry the blocked set so the row can be marked coral
  // and the message can NAME the field and open its editor.
  const formatBad = useMemo(() => new Set(formatBlocking || []), [formatBlocking]);
  const formatBadFields = useMemo(
    () => (panelFields as unknown as SchemaField[]).filter((f) => formatBad.has(f.name)),
    [panelFields, formatBad]
  );

  // ── The batches ───────────────────────────────────────────────────────────
  // The panel has always grouped its rows by the form's own section labels;
  // what it lacked was the onboarding wizard's SHAPE — numbered batches with a
  // count, and the page opening on what still needs you. Michael asked for the
  // information after the card step to be batched the way initial onboarding
  // is (2026-10-19).
  //
  // Deliberately NOT a one-batch-per-screen stepper like the wizard: this page
  // ends in a signature and its own copy says "nothing else is required from
  // you — just review and sign", so putting Next clicks between the patient and
  // that signature would add work to a page whose job is to end quickly. The
  // batches are numbered, counted and collapsible on one scroll instead, and a
  // complete batch opens as a single summary line.
  const batchGroups = useMemo(
    () => _groupBySection(panelFields as unknown as SchemaField[]),
    [panelFields]
  );
  const batchNeedsAttention = useCallback(
    (f: SchemaField) => {
      const raw = values[f.name];
      const filled = raw != null && String(raw).trim() !== "";
      return formatBad.has(f.name) || (!filled && fieldRequired(f));
    },
    [values, formatBad, fieldRequired]
  );
  const batchIsFilled = useCallback(
    (f: SchemaField) => {
      const raw = values[f.name];
      return raw != null && String(raw).trim() !== "";
    },
    [values]
  );
  const { stats: batchStats, open: batchOpenDefault } = useMemo(
    () => batchStatuses(batchGroups, batchNeedsAttention, batchIsFilled),
    [batchGroups, batchNeedsAttention, batchIsFilled]
  );
  // Which batches the patient has open. null means "use the defaults"; once
  // they touch a header it is their choice and stays that way.
  //
  // The reset is keyed to the batch TITLES — i.e. a different form or schema —
  // and deliberately NOT to the open-by-default set, which changes the moment a
  // batch stops needing attention: keying it there would slam a batch shut the
  // instant the patient filled its last empty box, under their hands.
  const [openBatches, setOpenBatches] = useState<string[] | null>(null);
  const batchSeed = batchStats.map((s) => s.title).join("\u0000");
  useEffect(() => {
    setOpenBatches(null);
  }, [batchSeed]);
  const effectiveOpen = openBatches ?? batchOpenDefault;
  const isBatchOpen = (title: string) => effectiveOpen.includes(title);
  const toggleBatch = (title: string) =>
    setOpenBatches((prev) => {
      const cur = prev ?? batchOpenDefault;
      return cur.includes(title) ? cur.filter((x) => x !== title) : [...cur, title];
    });
  const batchesNeeding = batchStats.filter((s) => !s.complete).length;
  const openField = (f: SchemaField) => {
    focusFormField(f.name, f.label);
    setModalField(f);
  };

  // Patient-edited subset of `values`. Pre-filled rows the patient never
  // touched are dropped here so they don't round-trip back to the canonical
  // — that's how the previous simulation clobbered a 45-char patient_address
  // with the 14-char street fragment the patient retyped into the panel.
  // If a row was never pre-filled (no entry in initialValues), the patient
  // typed it from scratch, so include it.
  const submittedValues = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      const initial = initialValues[k];
      if (initial !== undefined && String(initial) === String(v)) continue;
      out[k] = v;
    }
    return out;
  }, [values, initialValues]);

  // Signature payload for the inline + mobile-sticky submit — one object so
  // both submit paths stay identical. The typed name is rasterized to a PNG
  // here: the server-side PDF renderer has no cursive font, so without an image
  // the fax shows the name in plain Helvetica instead of the script the patient
  // saw on screen. (Type-only since 2026-08-26 — drawing was removed; the
  // signature is always the typed cursive PNG plus the legal name.)
  const buildSignaturePayload = () => ({
    name: signatureName.trim(),
    font: signatureFont,
    image_b64: typedSignatureToPng(signatureName.trim(), signatureFont),
    source: "patient_typed",
  });

  // What actually goes out as field_values: the patient-edited values PLUS
  // every EXPLICITLY signed signature line, written by name. The SPA is the
  // authoritative writer for signature placement (imp_31e369a1 — each line is
  // signed deliberately, nothing auto-copied); bridge_sync's first-match
  // hoist remains only as a fallback for legacy submissions and never
  // overwrites these.
  const submittedWithSignatures = () => {
    const out: Record<string, string> = { ...submittedValues };
    const name = signatureName.trim();
    if (!name) return out;
    if (multiSig) {
      for (const t of signatureTargets) {
        if (signedSigs[t.name]) out[t.name] = name;
      }
    } else if (signatureConfirmed && signatureTarget?.name) {
      out[signatureTarget.name] = name;
    }
    return out;
  };

  // Decide whether the leading questionnaire has anything to ask (only the
  // current signer's still-empty questions). Hook must run before early returns.
  // Prefer the backend-built question-model (single source of truth); fall back
  // to the legacy client builder for assignments sealed before it existed.
  const hasBackendQuestions = Array.isArray(backendQuestions) && backendQuestions.length > 0;
  const questionnaire = useMemo(
    () =>
      hasBackendQuestions
        ? renderBackendQuestions(backendQuestions, { fields: schemaFields, answers: values })
        : buildQuestionnaire(schemaFields, { audience: signerAudience, answers: values }),
    [hasBackendQuestions, backendQuestions, schemaFields, signerAudience, values]
  );
  function handleQuestionnaireComplete(out: Record<string, unknown>) {
    // Merge the answered/cleared field values, then drop into review + sign.
    setValues((prev) => ({ ...prev, ...((out || {}) as Record<string, string>) }));
    setQuestionnaireDone(true);
  }

  if (status === STATUS.LOADING) {
    return _loadingView("Getting your form ready…");
  }
  if (status === STATUS.ERROR) {
    return _centered(error || "Something went wrong loading your form.");
  }

  // Leading questionnaire step: when the form carries patient-owned questions
  // (audience-tagged in the durable model), ask them in plain language first,
  // then merge the answers into `values` and fall through to review + sign.
  // Empty for forms without the model, so behaviour is unchanged there.
  if (!questionnaireDone && questionnaire.questions.length > 0) {
    return (
      <BridgeQuestionnaire
        questions={hasBackendQuestions ? backendQuestions : null}
        fields={schemaFields}
        audience={signerAudience}
        onComplete={handleQuestionnaireComplete}
        onBack={onBack as (() => void) | undefined}
      />
    );
  }

  return (
    <div className="hf-bridge-root">
      {/* ─────────────────────────────────────────────────────────────
          RESPONSIVE LAYOUT STYLES (Agent C — mobile/tablet)
          Pure CSS @media queries so the layout snaps at breakpoints
          without JS re-renders on resize. Class names are scoped with
          a hf-bridge- prefix to avoid collisions with the SPA shell.
          Breakpoints:
            ≤ 640px  — mobile: stacked, PDF on top (collapsible), sticky submit
            641-1024px — tablet: side-by-side, sidebar 300px
            ≥ 1025px — desktop: side-by-side, sidebar 360px (legacy 420px trimmed)
          Touch targets: ≥44px on mobile (Apple HIG). Inputs use 16px font
          to suppress iOS auto-zoom on focus.
          ───────────────────────────────────────────────────────────── */}
      <style>{`
        .hf-bridge-root {
          display: flex;
          flex-direction: row;
          min-height: 100vh;
          background-color: #f5f0eb;
          /* nowrap so the sign panel can never wrap off below the PDF on
             mid-width screens — the PDF column scrolls instead (see main). */
          flex-wrap: nowrap;
        }
        .hf-bridge-aside {
          flex: 0 0 360px;
          max-width: 100%;
          min-width: 320px;
          border-right: 1px solid rgba(42, 77, 105, 0.1);
          background-color: white;
          overflow-y: auto;
          max-height: 100vh;
          box-sizing: border-box;
        }
        .hf-bridge-aside.hf-bridge-aside--profile { padding: 0; }
        .hf-bridge-aside.hf-bridge-aside--fill { padding: 24px; }
        .hf-bridge-main {
          flex: 1 1 auto;
          padding: 24px;
          min-width: 0;
          box-sizing: border-box;
          overflow-x: auto;
        }
        .hf-bridge-pdf-toggle { display: none; }
        .hf-bridge-pdf-wrap { display: block; }

        /* Tablet — shrink sidebar to give the PDF more room. */
        @media (max-width: 1024px) and (min-width: 641px) {
          .hf-bridge-aside { flex: 0 0 300px; min-width: 280px; }
          .hf-bridge-main { padding: 16px; }
        }

        /* Mobile — single column, PDF on top (collapsible). */
        @media (max-width: 640px) {
          .hf-bridge-root { flex-direction: column; }
          .hf-bridge-aside {
            flex: 1 1 auto;
            order: 2;
            max-height: none;
            min-width: 0;
            width: 100%;
            border-right: none;
            border-top: 1px solid rgba(42, 77, 105, 0.1);
            /* Pad bottom for the sticky submit overlay + iOS safe area. */
            padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
          }
          .hf-bridge-aside.hf-bridge-aside--fill { padding: 16px; }
          .hf-bridge-main {
            order: 1;
            padding: 12px;
            background-color: #f5f0eb;
          }
          .hf-bridge-pdf-toggle {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            min-height: 44px;
            padding: 12px 16px;
            background-color: white;
            border: 1px solid rgba(42, 77, 105, 0.15);
            border-radius: 10px;
            color: #182a33;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0,0,0,0.04);
            box-sizing: border-box;
          }
          .hf-bridge-pdf-wrap.hf-bridge-pdf-wrap--collapsed { display: none; }
          .hf-bridge-pdf-wrap { margin-top: 12px; }

          /* Form-field tap targets — full width, 16px+ to block iOS zoom. */
          .hf-bridge-aside button,
          .hf-bridge-aside input[type="text"],
          .hf-bridge-aside input[type="email"],
          .hf-bridge-aside input[type="tel"],
          .hf-bridge-aside input[type="date"],
          .hf-bridge-aside select {
            font-size: 16px !important;
            min-height: 44px;
          }
          .hf-bridge-aside ul li { margin-bottom: 12px !important; }

          /* Sticky submit — keep the primary action above the fold. */
          .hf-bridge-submit-mobile {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            padding: 12px 16px;
            padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
            background-color: rgba(255,255,255,0.97);
            border-top: 1px solid rgba(42, 77, 105, 0.12);
            box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
            z-index: 60;
            backdrop-filter: saturate(140%) blur(6px);
            -webkit-backdrop-filter: saturate(140%) blur(6px);
          }
          .hf-bridge-submit-mobile button {
            width: 100%;
            min-height: 48px;
            font-size: 16px !important;
          }
          /* Hide the inline submit on mobile; the sticky one takes over. */
          .hf-bridge-submit-inline { display: none; }
        }

        /* Full-viewport modal on mobile (centered card on tablet+). */
        @media (max-width: 640px) {
          .hf-bridge-modal-shell {
            padding: 0 !important;
            align-items: stretch !important;
          }
          .hf-bridge-modal-card {
            max-width: none !important;
            width: 100% !important;
            min-height: 100vh;
            border-radius: 0 !important;
            padding: 20px !important;
            padding-top: calc(20px + env(safe-area-inset-top, 0px)) !important;
            padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important;
            display: flex;
            flex-direction: column;
          }
          .hf-bridge-modal-card input[type="text"] { font-size: 16px !important; }
          .hf-bridge-modal-card button { min-height: 44px; }
        }
      `}</style>

      {/* Side panel — sticky on desktop, stacked on mobile. */}
      <aside
        className={
          "hf-bridge-aside " +
          (viewMode === "profile"
            ? "hf-bridge-aside--profile"
            : "hf-bridge-aside--fill")
        }
      >
        {viewMode === "profile" ? (
          <ProfileEditor
            initialProfile={initialProfile}
            onChange={handleProfileChange}
            onContinue={() => setViewMode("fill")}
            focusFieldKey={focusFieldKey}
          />
        ) : (
          <>
        {initialProfile && (
          <div
            style={{
              fontSize: 12,
              color: "#5b6e7c",
              marginBottom: 12,
              padding: "8px 10px",
              border: "1px solid rgba(42,77,105,0.12)",
              borderRadius: 8,
              backgroundColor: "#f5f0eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>
              {editedKeys.length > 0 ? (
                <>
                  {editedKeys.length} demographic{editedKeys.length === 1 ? "" : "s"} edited
                  {updateMyFile ? " · will be saved to file" : " · this form only"}
                </>
              ) : (
                "Demographics on file"
              )}
            </span>
            <button
              type="button"
              onClick={() => setViewMode("profile")}
              style={{
                background: "none",
                border: "none",
                color: "#182a33",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {editedKeys.length > 0 ? "edit" : "Edit my info"}
            </button>
          </div>
        )}
        {requiredPanel.length > 0 ? (
          <>
            <h2 style={{ marginTop: 0, color: "#182a33" }}>{reviewHeading}</h2>
            <p style={{ color: "#182a33", fontSize: 14, marginTop: 0, lineHeight: 1.5 }}>
              {reviewIntro}
            </p>
            <ol style={{ color: "#182a33", fontSize: 14, marginTop: 4, paddingLeft: 20, lineHeight: 1.6 }}>
              <li>Review the filled-in form {previewWhere}</li>
              <li>Complete the required fields below</li>
              <li>Sign at the bottom and submit</li>
            </ol>
            {(() => {
              const total = requiredPanel.length;
              const done = requiredFilledCount;
              const pct = total > 0 ? Math.round((done / total) * 100) : 100;
              const complete = done >= total;
              return (
                <div style={{ marginTop: 12, marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <span style={{ color: "#5b6e7c", fontSize: 13 }}>
                      {complete ? "All required fields complete" : `${done} of ${total} required fields complete`}
                    </span>
                    {complete && (
                      <span style={{ color: "#6b9c8e", fontSize: 13, fontWeight: 600 }}>✓ Ready to sign</span>
                    )}
                  </div>
                  <div style={{ height: 6, borderRadius: 999, backgroundColor: "#e6dccb", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, borderRadius: 999, backgroundColor: complete ? "#6b9c8e" : "#1b5e6e", transition: "width 250ms ease" }} />
                  </div>
                  <p style={{ color: "#7e8c96", fontSize: 12, marginTop: 8, marginBottom: 0 }}>Tap any item below to review or edit it.</p>
                </div>
              );
            })()}
          </>
        ) : (
          <>
            <h2 style={{ marginTop: 0, color: "#182a33" }}>{reviewHeading}</h2>
            <p style={{ color: "#182a33", fontSize: 14, marginTop: 0, lineHeight: 1.5 }}>
              {reviewIntro}
            </p>
            <ol style={{ color: "#182a33", fontSize: 14, marginTop: 4, paddingLeft: 20, lineHeight: 1.6 }}>
              <li>Review the form {previewWhere} to confirm details are correct</li>
              <li>Sign at the bottom — your typed name becomes your legal signature</li>
              <li>Click Submit to send the signed form back to your physician</li>
            </ol>
            <p style={{ color: "#5b6e7c", fontSize: 13, marginTop: 12, marginBottom: 16, fontStyle: "italic" }}>
              {panelFields.length > 0
                ? "Nothing else is required from you — the fields below are optional. Just review and sign."
                : "No additional fields required from you — just review and sign."}
            </p>
          </>
        )}

        {/* Grouped by the form's own section labels. Confirmed rows sit flat
            with a hairline divider; rows that still need the patient's input get
            a soft orange-accented card so they stand out. Label is a small muted
            caption; the value is the hero (the patient is confirming answers). */}
        {batchStats.length > 1 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              margin: "0 0 14px",
              fontSize: 12,
            }}
          >
            {batchStats.map((s) => (
              <button
                key={s.title}
                type="button"
                onClick={() => {
                  if (!isBatchOpen(s.title)) toggleBatch(s.title);
                  document
                    .getElementById(`batch-${s.index}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={{
                  border: `1px solid ${s.complete ? "#d8dee3" : "#e8943a"}`,
                  background: s.complete ? "#ffffff" : "#fff6ec",
                  color: "#182a33",
                  borderRadius: 999,
                  padding: "4px 10px",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                <span style={{ color: "#5b6e7c" }}>{s.index + 1}.</span> {s.title}
                {s.complete ? " ✓" : ` · ${s.attention}`}
              </button>
            ))}
          </div>
        )}

        {batchGroups.map((group, gi) => {
          const stat = batchStats[gi];
          const open = isBatchOpen(group.title);
          return (
          <div key={group.title} id={`batch-${gi}`} style={{ marginBottom: 18 }}>
            <button
              type="button"
              onClick={() => toggleBatch(group.title)}
              aria-expanded={open}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                width: "100%",
                textAlign: "left",
                border: "none",
                background: "transparent",
                padding: 0,
                margin: "0 0 6px 2px",
                cursor: "pointer",
                font: "inherit",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: "#a08f76",
                }}
              >
                {batchStats.length > 1 ? `${gi + 1} of ${batchStats.length} · ` : ""}
                {group.title}
              </span>
              <span style={{ fontSize: 12, color: stat.complete ? "#5b6e7c" : "#c2690f" }}>
                {stat.complete
                  ? `${stat.total} confirmed`
                  : `${stat.attention} still needs you`}
              </span>
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#5b6e7c" }}>
                {open ? "Hide" : "Show"}
              </span>
            </button>
            <ul hidden={!open} style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {group.fields.map((f) => {
                const raw = values[f.name];
                const filled = raw != null && String(raw).trim() !== "";
                const badFormat = formatBad.has(f.name);
                const needsAttention = badFormat || (!filled && fieldRequired(f));
                // Coral for a malformed value, amber for required-but-empty.
                const accent = badFormat ? "#c25450" : "#e8943a";
                const accentEdge = badFormat ? "rgba(194,84,80,0.55)" : "rgba(232,148,58,0.55)";
                const accentWash = badFormat ? "rgba(194,84,80,0.07)" : "rgba(232,148,58,0.06)";
                const accentText = badFormat ? "#a6403c" : "#a66222";
                const yn = _ynSide(f.name);
                return (
                  <li key={f.name} style={{ marginBottom: needsAttention ? 8 : 0 }}>
                    <button
                      type="button"
                      onMouseEnter={() => focusFormField(f.name, f.label)}
                      onFocus={() => focusFormField(f.name, f.label)}
                      onClick={() => { focusFormField(f.name, f.label); setModalField(f); }}
                      style={{
                        position: "relative",
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                        color: "#182a33",
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        padding: needsAttention ? "11px 30px 11px 13px" : "9px 28px 9px 2px",
                        border: needsAttention ? `1px solid ${accentEdge}` : "none",
                        borderLeft: needsAttention ? `3px solid ${accent}` : "none",
                        borderBottom: needsAttention ? `1px solid ${accentEdge}` : "1px solid #ece4d6",
                        borderRadius: needsAttention ? 8 : 0,
                        backgroundColor: needsAttention ? accentWash : "transparent",
                      }}
                    >
                      {/* Label caption (with a small sage check when confirmed) */}
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: 12,
                          fontWeight: 600,
                          color: needsAttention ? accentText : "#7e8c96",
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            flexShrink: 0,
                            width: 14,
                            color: badFormat ? accent : filled ? "#6b9c8e" : "#c79a5e",
                            fontSize: 12,
                            lineHeight: "14px",
                            textAlign: "center",
                          }}
                        >
                          {badFormat ? "!" : filled ? "✓" : "○"}
                        </span>
                        <span style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>{f.label}</span>
                      </span>
                      {/* Value — the hero. Yes/No renders as a pill. */}
                      {yn && filled ? (
                        <span
                          style={{
                            marginLeft: 21,
                            alignSelf: "flex-start",
                            display: "inline-block",
                            padding: "2px 12px",
                            borderRadius: 999,
                            fontSize: 13,
                            fontWeight: 600,
                            backgroundColor: "#e4ecee",
                            color: "#1b5e6e",
                          }}
                        >
                          {yn}
                        </span>
                      ) : (
                        <span
                          style={{
                            marginLeft: 21,
                            fontSize: 15,
                            overflowWrap: "anywhere",
                            color: badFormat ? accentText : filled ? "#182a33" : needsAttention ? accentText : "#9aa6ae",
                            fontStyle: filled ? "normal" : "italic",
                          }}
                        >
                          {filled ? _formatFieldValue(f.name, raw) : needsAttention ? "Tap to add" : "Optional"}
                        </span>
                      )}
                      {badFormat && (
                        <span
                          style={{
                            marginLeft: 21,
                            marginTop: 2,
                            fontSize: 12,
                            fontWeight: 600,
                            color: accentText,
                          }}
                        >
                          {_formatHint(f)} — tap to fix
                        </span>
                      )}
                      {/* Tap-to-edit affordance */}
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          right: needsAttention ? 12 : 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#c4b8a4",
                          fontSize: 13,
                        }}
                      >
                        ✎
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          );
        })}

        {/* Signature pad — always shown. Patient must type their name AND
            tick the confirmation box before submit is enabled. */}
        <div
          style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: "1px solid rgba(42, 77, 105, 0.1)",
          }}
        >
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#182a33",
              marginBottom: 6,
            }}
          >
            Sign here
          </label>
          <style>{`
            @keyframes hf-sig-pulse {
              0%   { box-shadow: 0 0 0 0 rgba(230, 165, 80, 0.4); }
              50%  { box-shadow: 0 0 0 6px rgba(230, 165, 80, 0.18); }
              100% { box-shadow: 0 0 0 0 rgba(230, 165, 80, 0); }
            }
            .hf-sig-zone .hf-sig-input,
            .hf-sig-zone .hf-sig-preview { transition: box-shadow 200ms ease; }
            .hf-sig-zone:focus-within .hf-sig-input,
            .hf-sig-zone:focus-within .hf-sig-preview {
              animation: hf-sig-pulse 1.5s ease-in-out infinite;
              border-color: rgba(230, 165, 80, 0.6);
            }
            .hf-sig-zone .hf-sig-hint {
              max-height: 0;
              opacity: 0;
              overflow: hidden;
              transition: max-height 200ms ease, opacity 200ms ease, margin 200ms ease;
              margin: 0;
            }
            .hf-sig-zone:focus-within .hf-sig-hint {
              max-height: 40px;
              opacity: 1;
              margin: -4px 0 8px 0;
            }
            @media (prefers-reduced-motion: reduce) {
              .hf-sig-zone:focus-within .hf-sig-input,
              .hf-sig-zone:focus-within .hf-sig-preview {
                animation: none;
                box-shadow: 0 0 0 3px rgba(230, 165, 80, 0.25);
              }
              .hf-sig-zone .hf-sig-hint { transition: none; }
            }
          `}</style>
          <div
            className="hf-sig-zone"
            ref={signerRef}
            onMouseEnter={focusSignature}
            onFocus={focusSignature}
          >
          {/* Type-only signature (drawing removed 2026-08-26 — drawn marks
              rasterized poorly on the faxed PDF). The patient types their legal
              name; it renders below in a cursive font and is what gets written
              into the PDF signature box. */}
          <input
            type="text"
            className="hf-sig-input"
            value={signatureName}
            placeholder="Type your full name"
            aria-label="Type your full name"
            onChange={(e) => {
              setSignatureName(e.target.value);
              if (signatureConfirmed) setSignatureConfirmed(false);
              // A changed name un-signs every explicitly-signed line (and
              // clears its date) — the old confirmations covered a different
              // typed signature.
              if (multiSig && Object.keys(signedSigs).length > 0) {
                for (const [n, on] of Object.entries(signedSigs)) {
                  if (on) stampSigDate(n, false);
                }
                setSignedSigs({});
              }
            }}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: 16,
              border: "1px solid #e6dccb",
              borderRadius: 8,
              boxSizing: "border-box",
              color: "#182a33",
              marginBottom: 10,
            }}
          />
          <div
            className="hf-sig-preview"
            style={{
              padding: "16px 12px",
              borderRadius: 8,
              border: "1px dashed #e6dccb",
              backgroundColor: "#f5f0eb",
              textAlign: "center",
              fontFamily: `'${signatureFont}', cursive`,
              fontSize: 28,
              color: "#182a33",
              minHeight: 56,
              marginBottom: 10,
            }}
          >
            {signatureName.trim() || (
              <span style={{ fontFamily: "system-ui", fontSize: 13, color: "#bbb" }}>
                Your signature preview will appear here
              </span>
            )}
          </div>
          <p
            className="hf-sig-hint"
            style={{
              fontSize: 12,
              fontStyle: "italic",
              color: "#5b6e7c",
              lineHeight: 1.4,
            }}
          >
            Your typed name becomes your legal signature on this form.
          </p>
          </div>
          {!multiSig ? (
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 13,
                color: "#5b6e7c",
                lineHeight: 1.4,
                cursor: signatureName.trim() ? "pointer" : "not-allowed",
              }}
            >
              <input
                type="checkbox"
                checked={signatureConfirmed}
                disabled={!signatureName.trim()}
                onChange={(e) => {
                  setSignatureConfirmed(e.target.checked);
                  stampSigDate(signatureTarget?.name, e.target.checked);
                }}
                style={{ marginTop: 2 }}
              />
              <span>{confirmStatement}</span>
            </label>
          ) : (
            <div>
              {/* Multi-line signing: the form asks this signer to sign in more
                  than one place. Each line is signed EXPLICITLY, in order —
                  ticking one jumps the PDF to that line so the signer sees the
                  section they are signing, and the cue then moves to the next
                  line. Nothing is auto-copied (imp_31e369a1). */}
              <p style={{ fontSize: 13, color: "#5b6e7c", lineHeight: 1.4, marginBottom: 8 }}>
                This form asks you to sign in {signatureTargets.length} places.
                Sign each one — tap a line to see where it is on the form.
              </p>
              {signatureTargets.map((t, idx) => {
                const signed = !!signedSigs[t.name];
                const isNext = nextUnsignedTarget?.name === t.name;
                return (
                  <label
                    key={t.name}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: 13,
                      color: "#5b6e7c",
                      lineHeight: 1.4,
                      cursor: signatureName.trim() ? "pointer" : "not-allowed",
                      padding: "8px 10px",
                      marginBottom: 6,
                      borderRadius: 8,
                      border: isNext && !signed
                        ? "1.5px solid rgba(37,107,41,0.9)"
                        : "1px solid #e6dccb",
                      backgroundColor: signed ? "#eef4ee" : "#fff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={signed}
                      disabled={!signatureName.trim()}
                      onChange={(e) => {
                        setSignedSigs((prev) => ({ ...prev, [t.name]: e.target.checked }));
                        stampSigDate(t.name, e.target.checked);
                        // Walk the signer to the line they just signed (so they
                        // see it land), then the cue moves to the next unsigned
                        // line via nextUnsignedTarget on re-render.
                        focusSignatureLine(t);
                      }}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <strong style={{ color: "#182a33" }}>
                        Signature {idx + 1} of {signatureTargets.length}
                      </strong>{" "}
                      — {_humanize(t.name)} (page {t.page}).{" "}
                      {idx === 0
                        ? confirmStatement
                        : "I am signing this section as well with the same legal signature."}
                      {signed ? " ✓ Signed" : ""}
                    </span>
                  </label>
                );
              })}
              {!allSignaturesConfirmed && signatureName.trim() ? (
                <p style={{ fontSize: 12, color: "#5b6e7c", fontStyle: "italic" }}>
                  All {signatureTargets.length} signature lines are needed before
                  the form can be submitted.
                </p>
              ) : null}
            </div>
          )}
          <link
            href="https://fonts.googleapis.com/css2?family=Dancing+Script&family=Great+Vibes&family=Sacramento&family=Allura&display=swap"
            rel="stylesheet"
          />
        </div>

        {/* Inline submit — visible on tablet+ (CSS hides on mobile). */}
        <div className="hf-bridge-submit-inline">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              onComplete?.(
                submittedWithSignatures(),
                buildSignaturePayload(),
                // Profile patch — bridged through from ProfileEditor. Null
                // when no preloaded profile was supplied (legacy callers).
                editedProfile
                  ? {
                      profile: editedProfile,
                      update_my_file: updateMyFile,
                      edited_keys: editedKeys,
                    }
                  : null
              )
            }
            style={{
              marginTop: 24,
              width: "100%",
              padding: "12px 14px",
              fontSize: 16,
              fontWeight: 600,
              border: "none",
              borderRadius: 8,
              backgroundColor: canSubmit ? "#182a33" : "#bbb",
              color: "white",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            Sign &amp; submit
          </button>
          {formatBadFields.length > 0 ? (
            <p style={{ color: "#c25450", fontSize: 12, marginTop: 10, textAlign: "center" }}>
              <button
                type="button"
                onClick={() => openField(formatBadFields[0])}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  color: "#c25450",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {formatBlockedNotice(formatBadFields)}
              </button>
            </p>
          ) : !signatureReady ? (
            <p style={{ color: "#5b6e7c", fontSize: 12, marginTop: 10, textAlign: "center" }}>
              Type your name and tick the box above to enable submit.
            </p>
          ) : null}
        </div>
          </>
        )}
      </aside>

      {/* PDF area. */}
      <main className="hf-bridge-main">
        {pdfDoc ? (
          <>
            {/* Mobile-only collapse toggle. CSS hides on tablet+. */}
            <button
              type="button"
              className="hf-bridge-pdf-toggle"
              aria-expanded={!pdfCollapsedMobile}
              aria-controls="hf-bridge-pdf-wrap"
              onClick={() => setPdfCollapsedMobile((v) => !v)}
            >
              <span>
                {pdfCollapsedMobile
                  ? "📄 Form preview (tap to expand)"
                  : "📄 Form preview"}
              </span>
              <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden="true">
                {pdfCollapsedMobile ? "›" : "×"}
              </span>
            </button>
            <div
              id="hf-bridge-pdf-wrap"
              className={
                "hf-bridge-pdf-wrap " +
                (pdfCollapsedMobile ? "hf-bridge-pdf-wrap--collapsed" : "")
              }
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                {/* ── BEGIN CLICK-TO-JUMP overlay wrapper (Agent B) ──
                    Position-relative wrapper around the canvas. The
                    overlay shares the canvas's exact rendered size by
                    living inside the same parent and using inset:0.
                    Mobile responsive sizing flows through automatically
                    since the canvas controls layout, not the overlay. */}
                <div
                  className="hf-pdf-stage"
                  style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}
                >
                  <canvas
                    ref={attachCanvas}
                    style={{
                      maxWidth: "100%",
                      display: "block",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                      background: "white",
                    }}
                  />
                  <PdfClickOverlay
                    targets={pdfClickTargets}
                    values={values}
                    signatureTarget={multiSig ? nextUnsignedTarget : signatureTarget}
                    signatureTargets={multiSig ? signatureTargets : []}
                    signedSigs={signedSigs}
                    signatureName={signatureName}
                    signatureFont={signatureFont}
                    signatureConfirmed={multiSig ? allSignaturesConfirmed : signatureConfirmed}
                    onSignClick={handleSignClick}
                    activeTarget={activeTarget}
                    currentPage={currentPage}
                    scale={pdfScale}
                    canvasEl={canvasRef.current}
                    onJump={jumpToProfileField}
                  />
                </div>
                {/* ── END CLICK-TO-JUMP overlay wrapper ─────────────── */}
              </div>
              {numPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    color: "#5b6e7c",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    ‹ Previous
                  </button>
                  <span>
                    Page {currentPage} of {numPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                    disabled={currentPage >= numPages}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ color: "#5b6e7c", padding: 32, textAlign: "center" }}>
            <p>The form preview isn't available for this assignment.</p>
            <p style={{ fontSize: 14 }}>
              You can still fill in the fields on the left and submit.
            </p>
          </div>
        )}
      </main>

      {/* Sticky mobile submit — only visible at ≤640px via CSS. Mirrors the
          inline submit's onComplete payload exactly. */}
      {viewMode !== "profile" && (
        <div className="hf-bridge-submit-mobile">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              onComplete?.(
                submittedWithSignatures(),
                buildSignaturePayload(),
                editedProfile
                  ? {
                      profile: editedProfile,
                      update_my_file: updateMyFile,
                      edited_keys: editedKeys,
                    }
                  : null
              )
            }
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: 16,
              fontWeight: 600,
              border: "none",
              borderRadius: 10,
              backgroundColor: canSubmit ? "#182a33" : "#bbb",
              color: "white",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {canSubmit ? "Sign & submit" : "Sign to enable submit"}
          </button>
          {formatBadFields.length > 0 && (
            <button
              type="button"
              onClick={() => openField(formatBadFields[0])}
              style={{
                display: "block",
                width: "100%",
                marginTop: 8,
                background: "none",
                border: "none",
                padding: 0,
                fontSize: 12,
                color: "#c25450",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {formatBlockedNotice(formatBadFields)}
            </button>
          )}
        </div>
      )}

      {/* Modal for editing one field at a time. */}
      {modalField && (
        <FieldEditorModal
          field={modalField}
          initialValue={values[modalField.name] || ""}
          onCancel={() => setModalField(null)}
          onSave={(v) => {
            setValue(modalField.name, v);
            setModalField(null);
          }}
        />
      )}
    </div>
  );
}


interface FieldEditorModalProps {
  field: SchemaField;
  initialValue: string;
  onCancel: () => void;
  onSave: (v: string) => void;
}

// Exported for BridgeFieldsForm.fieldEditor.test.tsx only.
export function FieldEditorModal({ field, initialValue, onCancel, onSave }: FieldEditorModalProps) {
  // As-you-type masking: backend-sealed input_format wins, else infer from the
  // field. When set, the input formats live and blocks Save on a malformed
  // non-empty value — EXCEPT health_card_ns, which warns but never blocks
  // (out-of-province cards don't match the MSI 10-digit shape).
  const fmt = field.input_format || inferInputFormat(field);
  const hasFmt = fmt && FORMATS[fmt];
  const isHcn = fmt === "health_card_ns";

  // Seed, validate and save the DISPLAYED form (case imp_563aea68). The box
  // renders formatValue(raw) but Save used to validate the raw stored value,
  // so a value differing from its display form only by punctuation looked
  // right, failed, and Save did nothing — the PR #337 seed/validate mismatch,
  // surviving here. Seeding through the formatter makes raw === displayed
  // from the first render; saving the displayed form writes it normalised.
  const [value, setValue] = useState(() =>
    hasFmt ? String(formatValue(fmt, initialValue) ?? "") : initialValue
  );
  const [touched, setTouched] = useState(false);
  const displayVal = hasFmt ? String(formatValue(fmt, value) ?? "") : value;
  const formatInvalid = hasFmt && !isFormatValid(fmt, displayVal);
  const blocksOnFormat = formatInvalid && !isHcn;

  function submit(ev?: React.FormEvent) {
    ev?.preventDefault?.();
    setTouched(true);
    const trimmed = displayVal.trim();
    if (!trimmed && field.required) return;
    if (trimmed && blocksOnFormat) return;
    onSave(trimmed);
  }

  const placeholder = hasFmt ? FORMATS[fmt].placeholder : _placeholderFor(field.name);

  return (
    <div
      onClick={onCancel}
      className="hf-bridge-modal-shell"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(20, 30, 40, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 100,
      }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="hf-bridge-modal-card"
        style={{
          backgroundColor: "white",
          padding: 24,
          borderRadius: 12,
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ marginTop: 0, color: "#182a33" }}>{field.label}</h3>
        <input
          autoFocus
          type="text"
          value={displayVal}
          placeholder={placeholder}
          inputMode={hasFmt ? (FORMATS[fmt].inputMode as React.HTMLAttributes<HTMLInputElement>["inputMode"]) : undefined}
          maxLength={hasFmt ? FORMATS[fmt].maxLength : undefined}
          autoComplete={hasFmt ? FORMATS[fmt].autoComplete : undefined}
          onChange={(e) =>
            hasFmt
              ? maskedChange(fmt, displayVal, e, (v: string) => setValue(v))
              : setValue(e.target.value)
          }
          style={{
            width: "100%",
            padding: "12px 14px",
            fontSize: 16,
            border: `1px solid ${blocksOnFormat ? "#c25450" : "#e6dccb"}`,
            borderRadius: 8,
            boxSizing: "border-box",
            color: "#182a33",
          }}
        />
        {touched && !value.trim() && field.required && (
          <p style={{ color: "#c25450", fontSize: 13, margin: "10px 0 0" }}>
            This field is required.
          </p>
        )}
        {formatInvalid && displayVal.trim() && (
          <p style={{ color: isHcn ? "#5b6e7c" : "#c25450", fontSize: 13, margin: "10px 0 0" }}>
            {validationMessage(fmt, displayVal)}
          </p>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "10px 16px",
              border: "1px solid #e6dccb",
              borderRadius: 8,
              backgroundColor: "white",
              color: "#182a33",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={(blocksOnFormat && value.trim().length > 0) as boolean | undefined}
            style={{
              padding: "10px 18px",
              border: "none",
              borderRadius: 8,
              backgroundColor: blocksOnFormat && value.trim().length > 0 ? "#bbb" : "#182a33",
              color: "white",
              cursor: blocksOnFormat && value.trim().length > 0 ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}


function _centered(content: React.ReactNode) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
      }}
    >
      <p style={{ color: "#5b6e7c", maxWidth: 420, textAlign: "center" }}>{content}</p>
    </div>
  );
}

// Branded loading view: the animated Parseo mosaic (each tile soft-pulses in a
// staggered clockwise ripple, respecting reduced-motion) above a caption.
function _loadingView(caption: React.ReactNode) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
        gap: 16,
      }}
    >
      <ParseoLogo animated size={72} />
      <p style={{ color: "#5b6e7c", maxWidth: 420, textAlign: "center", margin: 0 }}>{caption}</p>
    </div>
  );
}


function _humanize(snake: string) {
  return snake
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// What is wrong with a value that fails its wired format, in the format's own
// words ("Enter a 10-digit phone number"). Same key resolution the sign gate
// uses, so the row's hint and the gate can never name different formats.
// Exported for BridgeFieldsForm.formatBlock.test.ts.
export function _formatHint(f: SchemaField): string {
  const key = f.input_format && FORMATS[f.input_format] ? f.input_format : inferInputFormat(f);
  const spec = key ? FORMATS[key] : null;
  return spec?.message || "This value doesn't look complete";
}

/**
 * The submit line when a FILLED value fails its wired format. It NAMES the
 * field. The copy it replaces — "Please fix the highlighted field format
 * before submitting" — pointed at a highlight the panel never rendered (a
 * filled field was styled as confirmed, green tick and all) and at a row
 * usually scrolled out of view, so the patient was stopped with no way to
 * find the problem. Exported for BridgeFieldsForm.formatBlock.test.ts.
 */
export function formatBlockedNotice(
  fields: ReadonlyArray<{ label?: unknown; name?: unknown }>,
): string {
  if (!fields || fields.length === 0) return "";
  const first = String(fields[0]?.label || fields[0]?.name || "this field");
  return fields.length === 1
    ? `Check “${first}” before submitting`
    : `Check ${fields.length} fields before submitting, starting with “${first}”`;
}

// If a field is one half of a Yes/No pair, return the human answer ("Yes"/"No").
function _ynSide(name: string) {
  const m = String(name || "").match(/_(yes|no)$/i);
  return m ? (m[1].toLowerCase() === "yes" ? "Yes" : "No") : null;
}

// Present a stored value the way a person reads it: dates, phones, postal codes.
// Parsing is string-only (no Date arithmetic) to stay timezone-safe.
function _formatFieldValue(name: string, value: unknown) {
  const v = String(value == null ? "" : value).trim();
  if (!v) return v;
  const n = String(name || "").toLowerCase();
  // ISO date YYYY-MM-DD → "Jan 1, 2001"
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso && (n.includes("date") || n.includes("birth") || n.includes("dob"))) {
    const mi = parseInt(iso[2], 10) - 1;
    if (mi >= 0 && mi < 12) return `${_MONTHS[mi]} ${parseInt(iso[3], 10)}, ${iso[1]}`;
  }
  // Phone: 10 digits → (902) 300-5055 ; 11 with leading 1 → +1 (…)
  if (n.includes("phone") || n.includes("tel")) {
    const d = v.replace(/\D/g, "");
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length === 11 && d[0] === "1") return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  // Canadian postal code A1A1A1 → A1A 1A1
  if (n.includes("postal")) {
    const c = v.replace(/\s+/g, "").toUpperCase();
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(c)) return `${c.slice(0, 3)} ${c.slice(3)}`;
  }
  return v;
}

// Group the panel rows by the form's own section label (form-driven, not
// hardcoded per insurer). Fields without a section fall under "Your information".
// Preserves first-seen section order.
function _groupBySection(fields: SchemaField[]) {
  const order: string[] = [];
  const byTitle = new Map<string, SchemaField[]>();
  for (const f of fields) {
    const title = (f.section && String(f.section).trim()) || "Your information";
    if (!byTitle.has(title)) { byTitle.set(title, []); order.push(title); }
    byTitle.get(title)!.push(f);
  }
  return order.map((title) => ({ title, fields: byTitle.get(title)! }));
}


function _placeholderFor(name: string) {
  const n = name.toLowerCase();
  if (n.includes("phone")) return "e.g. (902) 555-1234";
  if (n.includes("postal")) return "e.g. B3J 1A1";
  if (n.includes("email")) return "e.g. you@example.com";
  if (n.includes("insurance") && n.includes("id")) return "Insurance member ID";
  if (n.includes("policy")) return "Policy / group number";
  if (n.includes("address")) return "Street address";
  if (n.includes("city")) return "City";
  if (n.includes("dob") || n.includes("birth")) return "YYYY-MM-DD";
  return "";
}


// ── BEGIN CLICK-TO-JUMP HELPERS (Agent B) ───────────────────────────────
// Translate a PDF field name (e.g. "Patient_Email_Address") to a
// ProfileEditor input key (snake_case demographic field). The mapping
// is deliberately fuzzy: we look at lowercased substrings so case and
// suffix variations all funnel to the right ProfileEditor input. Any
// PDF field that doesn't match returns null — caller treats that as
// a no-op so unknown fields don't crash the SPA.
function _pdfFieldNameToProfileKey(rawName: string | null | undefined): string | null {
  if (!rawName) return null;
  const n = String(rawName).toLowerCase();
  if (!n.includes("patient")) return null;

  // Health card variants — check FIRST so they don't get caught by the
  // generic name matcher below ("HCN" doesn't include "first"/"last").
  if (n.includes("health_card") || n.includes("healthcard") || n.includes("hcn"))
    return "health_care_number";

  // Insurance
  if (n.includes("insurance_provider") || n.includes("insurance_company"))
    return "insurance_provider";
  if (n.includes("insurance_id") || n.includes("member_id") || n.includes("policy_id"))
    return "insurance_id";
  // Primary plan policy/group number — fed by the Confirm Details `plan_number`
  // field. Guard against the OTHER-plan policy number (a separate, conditional
  // COB field that must never inherit the primary plan's number).
  if ((n.includes("policy_group") || n.includes("group_number") || n.includes("policy_number")) &&
      !n.includes("other"))
    return "plan_number";

  // Date of birth — check before generic "date" anywhere
  if (n.includes("date_of_birth") || n.includes("dob") || n.includes("birthdate"))
    return "date_of_birth";

  // Contact
  if (n.includes("email")) return "email";
  if (n.includes("phone")) return "phone";

  // Address parts — postal first so "Patient_Postal_Code" doesn't match
  // the generic address bucket.
  if (n.includes("postal")) return "postal_code";
  if (n.includes("province") || n.includes("state")) return "province";
  if (n.includes("city")) return "city";
  // Suite/unit — patient's only; never pull the prescriber's or pharmacy's suite
  // from the patient profile. Checked before the generic address bucket.
  if ((n.includes("suite") || n.includes("apartment")) && !n.includes("prescriber") && !n.includes("pharmacy"))
    return "suite";
  if (n.includes("street_address") || n.includes("address")) return "address";

  // Name — "full" name jumps to first_name (patient can split visually)
  if (n.includes("first_name") || n.includes("given_name")) return "first_name";
  if (
    n.includes("last_name") ||
    n.includes("surname") ||
    n.includes("family_name")
  )
    return "last_name";
  if (n.includes("full_name") || n.includes("patient_name")) return "first_name";

  return null;
}


/**
 * Invisible click overlay that sits on top of the PDF canvas. Renders
 * a transparent <button> per click target for the current page,
 * positioned via bbox × scale (top-origin PyMuPDF coords — no Y-flip).
 * Hover shows a soft amber tint so the patient discovers the feature
 * without it being visually noisy by default.
 */
interface PdfClickOverlayProps {
  targets: ClickTarget[];
  values: Record<string, string>;
  signatureTarget: SignatureTargetShape | null;
  // Multi-line signing (imp_31e369a1): every signature line this signer owns.
  // Empty for single-line forms — `signatureTarget` alone drives those.
  // On multi-line forms, `signatureTarget` is the NEXT unsigned line (the
  // pulsing cue) and this list paints the rest: signed lines render the
  // cursive signature, later unsigned lines a static outline.
  signatureTargets: SignatureTargetShape[];
  signedSigs: Record<string, boolean>;
  signatureName: string;
  signatureFont: string;
  signatureConfirmed: boolean;
  onSignClick: () => void;
  activeTarget: ActiveTargetShape | null;
  currentPage: number;
  scale: number;
  canvasEl: HTMLCanvasElement | null;
  onJump: (name: string) => void;
}

function PdfClickOverlay({ targets, values, signatureTarget, signatureTargets, signedSigs, signatureName, signatureFont, signatureConfirmed, onSignClick, activeTarget, currentPage, scale: rawScale, canvasEl, onJump }: PdfClickOverlayProps) {
  // The canvas is rendered at `rawScale` but DISPLAYED with maxWidth:100%, so in
  // a narrow (half-width) window it shrinks while the overlay would keep the
  // full scale — boxes float off the form. Scale positions by the displayed-to-
  // intrinsic ratio so they track the PDF. Ratio is 1 at full width (unchanged).
  const _renderW = canvasEl?.width || 0;
  const _dispW = canvasEl?.clientWidth || _renderW;
  const scale = _renderW > 0 ? rawScale * (_dispW / _renderW) : rawScale;
  const visible = (targets || []).filter((t) => Number(t.page) === Number(currentPage));
  // Unified "where you are" highlight: the focused panel field wins; otherwise
  // the signature field acts as the default sign-here cue.
  const hl =
    activeTarget && Array.isArray(activeTarget.bbox) && activeTarget.bbox.length >= 4
      ? activeTarget
      : signatureTarget && Array.isArray(signatureTarget.bbox) && signatureTarget.bbox.length >= 4
      ? { ...signatureTarget, label: "Sign here", kind: "signature" }
      : null;
  const hlVisible = hl && Number(hl.page) === Number(currentPage);
  if (visible.length === 0 && !hlVisible) return null;

  // Canvas might not be measured yet on the first render pass. When
  // missing, fall back to inset:0 so the overlay still claims the same
  // box as soon as the canvas reports its size on the next paint.
  const width = canvasEl?.clientWidth || canvasEl?.width || undefined;
  const height = canvasEl?.clientHeight || canvasEl?.height || undefined;

  return (
    <div
      className="hf-pdf-click-overlay"
      aria-hidden="false"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: width ? `${width}px` : "100%",
        height: height ? `${height}px` : "100%",
        pointerEvents: "none", // children opt in
      }}
    >
      <style>{`
        .hf-pdf-click-overlay button.hf-pdf-click-target {
          position: absolute;
          background: transparent;
          border: 1.5px solid transparent;
          border-radius: 4px;
          cursor: pointer;
          pointer-events: auto;
          padding: 0;
          margin: 0;
          transition: background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease;
        }
        .hf-pdf-click-overlay button.hf-pdf-click-target:hover,
        .hf-pdf-click-overlay button.hf-pdf-click-target:focus-visible {
          background: rgba(230, 165, 80, 0.18);
          border-color: rgba(230, 165, 80, 0.6);
          box-shadow: 0 0 0 3px rgba(230, 165, 80, 0.18);
          outline: none;
        }
        @keyframes hf-sign-pulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(37,107,41,0.55), 0 0 0 6px rgba(37,107,41,0.16); }
          50%      { box-shadow: 0 0 0 2px rgba(37,107,41,0.9),  0 0 0 11px rgba(37,107,41,0.28); }
        }
        .hf-pdf-click-overlay .hf-sign-here {
          position: absolute;
          border: 2px solid rgba(37,107,41,0.9);
          border-radius: 4px;
          background: rgba(37,107,41,0.10);
          animation: hf-sign-pulse 1.5s ease-in-out infinite;
          pointer-events: none;
        }
        .hf-pdf-click-overlay .hf-sign-here-label {
          position: absolute;
          left: 0;
          top: -19px;
          background: #578073;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          padding: 1px 7px;
          border-radius: 4px;
          white-space: nowrap;
        }
        .hf-pdf-click-overlay .hf-pdf-value {
          position: absolute;
          display: flex;
          align-items: center;
          box-sizing: border-box;
          padding: 0 2px;
          color: #16384b;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          pointer-events: none;
        }
        .hf-pdf-click-overlay .hf-pdf-value--check {
          justify-content: center;
          padding: 0;
          font-weight: 800;
        }
      `}</style>
      {hlVisible && hl!.kind !== "signature" && (() => {
        const [hx1, hy1, hx2, hy2] = hl!.bbox;
        const isSig = hl!.kind === "signature";
        const label = isSig ? "✍ Sign here" : (hl!.label ? String(hl!.label).slice(0, 24) : "");
        return (
          <div
            className="hf-sign-here"
            style={{
              left: `${hx1 * scale}px`,
              top: `${hy1 * scale}px`,
              width: `${Math.max(8, (hx2 - hx1) * scale)}px`,
              height: `${Math.max(8, (hy2 - hy1) * scale)}px`,
            }}
          >
            {label ? <span className="hf-sign-here-label">{label}</span> : null}
          </div>
        );
      })()}
      {/* Click-to-sign: the signature field on the form. Before signing it's a
          pulsing "Sign here" hotspot; tapping it jumps to the signer panel.
          Once the patient signs, their signature renders here on the form in
          the cursive font — so it appears on the page, like the physician's. */}
      {(() => {
        const sig =
          signatureTarget &&
          Number(signatureTarget.page) === Number(currentPage) &&
          Array.isArray(signatureTarget.bbox) &&
          signatureTarget.bbox.length >= 4
            ? signatureTarget
            : null;
        if (!sig) return null;
        const [sx1, sy1, sx2, sy2] = sig.bbox;
        const sw = Math.max(10, (sx2 - sx1) * scale);
        const sh = Math.max(10, (sy2 - sy1) * scale);
        // Only show the signature on the form once the patient has CONFIRMED it
        // (checked the box) — not just because a name was pre-filled. Until then
        // it's a "Sign here" cue.
        const signed = !!(signatureConfirmed && signatureName && String(signatureName).trim());
        return (
          <button
            type="button"
            onClick={onSignClick}
            title={signed ? "Signed — click to change" : "Click to sign"}
            style={{
              position: "absolute",
              left: `${sx1 * scale}px`,
              top: `${sy1 * scale}px`,
              width: `${sw}px`,
              height: `${sh}px`,
              pointerEvents: "auto",
              cursor: "pointer",
              padding: 0,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              overflow: "hidden",
              background: signed ? "transparent" : "rgba(37,107,41,0.10)",
              border: signed ? "1px solid transparent" : "2px solid rgba(37,107,41,0.9)",
              animation: signed ? "none" : "hf-sign-pulse 1.5s ease-in-out infinite",
            }}
          >
            {signed ? (
              // Typed signature rendered on the form in the cursive font — so it
              // appears on the page, like the physician's. (Type-only since
              // 2026-08-26; the drawn-mark path was removed.)
              <span
                style={{
                  fontFamily: `'${signatureFont || "Dancing Script"}', cursive`,
                  fontSize: `${Math.max(13, Math.min(sh * 0.85, 30))}px`,
                  color: "#16384b",
                  whiteSpace: "nowrap",
                  paddingLeft: 4,
                  lineHeight: 1,
                }}
              >
                {String(signatureName).trim()}
              </span>
            ) : (
              <span
                style={{
                  background: "#256b29",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "1px 7px",
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                }}
              >
                ✍ Sign here
              </span>
            )}
          </button>
        );
      })()}
      {/* Multi-line signing: the OTHER signature lines on this page (the next
          unsigned line renders above via `signatureTarget`). Signed lines show
          the cursive signature; unsigned-but-not-next lines show a static
          numbered outline so the signer can see every place they will sign. */}
      {(signatureTargets || [])
        .filter(
          (t) =>
            Number(t.page) === Number(currentPage) &&
            Array.isArray(t.bbox) &&
            t.bbox.length >= 4 &&
            t.name !== signatureTarget?.name
        )
        .map((t) => {
          const [sx1, sy1, sx2, sy2] = t.bbox;
          const sw = Math.max(10, (sx2 - sx1) * scale);
          const sh = Math.max(10, (sy2 - sy1) * scale);
          const signed = !!(signedSigs?.[t.name] && signatureName && String(signatureName).trim());
          const order = (signatureTargets || []).findIndex((x) => x.name === t.name) + 1;
          return (
            <div
              key={`sigline-${t.name}`}
              style={{
                position: "absolute",
                left: `${sx1 * scale}px`,
                top: `${sy1 * scale}px`,
                width: `${sw}px`,
                height: `${sh}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                overflow: "hidden",
                borderRadius: 4,
                pointerEvents: "none",
                background: signed ? "transparent" : "rgba(37,107,41,0.06)",
                border: signed ? "1px solid transparent" : "1.5px dashed rgba(37,107,41,0.55)",
              }}
            >
              {signed ? (
                <span
                  style={{
                    fontFamily: `'${signatureFont || "Dancing Script"}', cursive`,
                    fontSize: `${Math.max(13, Math.min(sh * 0.85, 30))}px`,
                    color: "#16384b",
                    whiteSpace: "nowrap",
                    paddingLeft: 4,
                    lineHeight: 1,
                  }}
                >
                  {String(signatureName).trim()}
                </span>
              ) : (
                <span
                  style={{
                    background: "#578073",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "1px 7px",
                    borderRadius: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  ✍ Signature {order}
                </span>
              )}
            </div>
          );
        })}
      {/* Filled-value layer — paints what the patient typed in the left panel
          onto the form, live, at each field's bbox. Non-interactive (the
          clickable hotspots below sit on top). Signature fields are left to
          the sign-here flow. */}
      {visible.map((t, i) => {
        const raw = values ? values[t.name] : undefined;
        if (raw == null || String(raw) === "") return null;
        const ftype = String(t.type || "").toLowerCase();
        if (ftype === "signature") return null;
        // A drawn-signature data-URI must never be painted as raw text (the
        // "data:image/png;base64,…" bug). If one lands on a non-signature field,
        // render the image instead. (Signature-typed fields already return null
        // above and are handled by the sign-here flow.)
        if (String(t.type || "").length >= 0 && String(values[t.name] || "").startsWith("data:image")) {
          const [bx1, by1, bx2, by2] = Array.isArray(t.bbox) && t.bbox.length >= 4 ? t.bbox : [0, 0, 0, 0];
          if (Number.isFinite(Number(bx1))) {
            return (
              <img
                key={`sigimg-${t.name}-${i}`}
                src={String(values[t.name])}
                alt="Signature"
                style={{
                  position: "absolute",
                  left: `${bx1 * scale}px`,
                  top: `${by1 * scale}px`,
                  width: `${Math.max(2, (bx2 - bx1) * scale)}px`,
                  height: `${Math.max(2, (by2 - by1) * scale)}px`,
                  objectFit: "contain",
                  pointerEvents: "none",
                }}
              />
            );
          }
          return null;
        }
        // Long free-text (rationale / large_text) is NOT painted onto the PDF —
        // it can't fit a single bbox and a bad/oversized box dumps it at the
        // page footer (the "way off" bug). It stays readable in the side panel.
        if (ftype === "textarea" || ftype === "large_text") return null;
        // Never position from an invalid bbox (→ NaN → CSS footer fallback).
        if (!Array.isArray(t.bbox) || t.bbox.length < 4 || !t.bbox.every((n) => Number.isFinite(Number(n)))) return null;
        const sval = String(raw);
        const low = sval.toLowerCase();
        const [x1, y1, x2, y2] = t.bbox;
        const left = x1 * scale;
        const top = y1 * scale;
        const w = Math.max(2, (x2 - x1) * scale);
        const h = Math.max(2, (y2 - y1) * scale);
        // Render a check for boolean checkbox/radio fields; everything else as
        // text. Without a known type, only treat small boxes with truthy
        // tokens as checks so a text field holding "1"/"yes" isn't mis-drawn.
        const isCheck =
          ftype === "checkbox" || ftype === "radio" ||
          (!ftype && w < 26 && h < 26 && ["true", "yes", "on", "x", "checked", "1"].includes(low));
        if (isCheck && ["false", "no", "off", "0", "unchecked"].includes(low)) return null;
        return (
          <div
            key={`val-${t.name}-${i}`}
            className={"hf-pdf-value" + (isCheck ? " hf-pdf-value--check" : "")}
            style={{
              left: `${left}px`,
              top: `${top}px`,
              width: `${w}px`,
              height: `${h}px`,
              fontSize: isCheck
                ? `${Math.max(8, Math.min(w, h) * 0.9)}px`
                : `${Math.max(7, Math.min(h * 0.72, 14))}px`,
            }}
          >
            {isCheck ? "✓" : sval}
          </div>
        );
      })}
      {visible.map((t, i) => {
        if (!Array.isArray(t.bbox) || t.bbox.length < 4 || !t.bbox.every((n) => Number.isFinite(Number(n)))) return null;
        const [x1, y1, x2, y2] = t.bbox;
        const left = x1 * scale;
        const top = y1 * scale;
        const w = Math.max(2, (x2 - x1) * scale);
        const h = Math.max(2, (y2 - y1) * scale);
        const profileKey = _pdfFieldNameToProfileKey(t.name);
        if (!profileKey) return null; // skip non-mapped PDF fields
        return (
          <button
            key={`${t.name}-${i}`}
            type="button"
            className="hf-pdf-click-target"
            title={`Edit ${_humanize(profileKey)}`}
            aria-label={`Edit ${_humanize(profileKey)}`}
            onClick={() => onJump?.(t.name)}
            style={{
              left: `${left}px`,
              top: `${top}px`,
              width: `${w}px`,
              height: `${h}px`,
            }}
          />
        );
      })}
    </div>
  );
}
// ── END CLICK-TO-JUMP HELPERS ───────────────────────────────────────────
